import { renderRichText } from "@storyblok/react";
import type { MultilinkStoryblok, RichtextStoryblok } from "@/component-types-sb";

/**
 * Erkennt Werte, die keine internen Pfade sind und deshalb nie ein "/"
 * vorangestellt bekommen duerfen.
 *
 * Hintergrund: Redakteure tragen externe Adressen auch in Felder ein, die als
 * story-Link ausgezeichnet sind. Ein stumpfes `/${cached_url}` erzeugte daraus
 * URLs wie /https:/co-clarity.de oder /partner/www.dexcom.com/de-de — beide
 * antworten mit 404.
 */
const ABSOLUTE_PREFIXES = ["http://", "https://", "mailto:", "tel:", "//"];

function isAbsolute(url: string) {
  return ABSOLUTE_PREFIXES.some((prefix) => url.startsWith(prefix));
}

/**
 * Domain ohne Schema, z.B. "www.dexcom.com/de-de" oder "co-clarity.de".
 *
 * Ein erster Pfadabschnitt mit Punkt ist im Storyblok-Baum kein gueltiger
 * Slug, sondern immer eine vergessene Domain.
 */
function looksLikeBareDomain(url: string) {
  const [firstSegment] = url.split("/");
  // Nur bekannte TLD-Formen: ein Slug wie "leitfaden-v1.2" enthaelt zwar
  // einen Punkt, ist aber kein Host. Ein Host endet im ersten Segment auf
  // eine Buchstaben-TLD (.de, .com, .co.uk) — ggf. mit Port.
  return /\.[a-z]{2,}(:\d+)?$/i.test(firstSegment);
}

/**
 * Baut aus einem Storyblok-Link die fertige href.
 *
 * Immer diese Funktion nutzen statt `/${link.cached_url}` zu interpolieren:
 * nur hier werden externe Adressen, fehlende Schemata und fehlende fuehrende
 * Slashes behandelt.
 */
export function getUrlFromLinkObject(link: MultilinkStoryblok | undefined) {
  if (!link) return "";

  if (link.linktype === "email") {
    return link.email ? `mailto:${link.email}` : "";
  }

  const raw = (
    link.linktype === "story"
      ? link.story?.full_slug || link.cached_url
      : link.url || link.cached_url
  )?.trim();

  if (!raw) return "";

  // Schon absolut (http://, mailto:, …) — unveraendert uebernehmen.
  if (isAbsolute(raw)) return raw;

  // Externe Adresse ohne Schema: "co-clarity.de" -> "https://co-clarity.de".
  // Assets liegen auf a.storyblok.com und sind immer absolut, fallen also
  // bereits oben raus.
  if (looksLikeBareDomain(raw)) return `https://${raw}`;

  // Interner Pfad: fuehrenden Slash garantieren, ohne ihn zu verdoppeln, und
  // den abschliessenden entfernen (Storyblok haengt ihn an Ordner-Startseiten
  // an, die Route erwartet ihn nicht).
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return path.length > 1 ? path.replace(/\/$/, "") : path;
}

/**
 * Wie getUrlFromLinkObject, haengt aber einen im Link gepflegten Anker an.
 *
 * Anker gelten nur fuer interne Ziele; bei externen URLs bringt der Redakteur
 * den Anker selbst mit.
 */
export function getUrlWithAnchor(link: MultilinkStoryblok | undefined) {
  const url = getUrlFromLinkObject(link);
  if (!url || !link?.anchor) return url;
  if (isAbsolute(url)) return url;
  return `${url}#${link.anchor}`;
}

/**
 * Renders a Storyblok field that may be either a plain string (legacy)
 * or a richtext object. Returns an HTML string.
 */
export function renderRichTextField(
  value: string | RichtextStoryblok | undefined,
): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  const html = renderRichText(value) || "";
  // Open all links in a new tab
  return html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
}
